import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { BDF_SERVICE, BdfJob, BdfJobStatus, BdfService } from '@infinitform/platform-core';
import { InlineSpinnerComponent } from '../spinner/inline-spinner.component';
import { TooltipDirective } from '../tooltip/tooltip.directive';

// s3Key (cloud) and localPath (desktop) are mutually exclusive — either signals
// a done file is present for the smartIndicator() check below.
type BdfPillJob = {
  status: string;
  s3Key?: string;
  localPath?: string;
  filename?: string;
  source?: 'organic' | 'prismatic' | 'analysis';
  progress?: number;
  stage?: string;
};

export interface BdfPillSource {
  id: string;
  bdfJob?: BdfPillJob | null;
  children?: Array<{
    id: string;
    bdfJob?: BdfPillJob | null;
  }>;
}

export type BdfPillSize = 'xxs' | 'xs' | 'sm';

// Single BDF status badge used everywhere (project lists, sharing lists, editor
// sidenav, modal child rows, modal single-project status). Two modes:
//   1. Smart:  pass [item] — the badge shows 'running' (with spinner) or
//              'done', and nothing otherwise. Used by list rows.
//   2. Direct: pass [status] (and optional [text]) — the badge shows any of
//              the 5 BDF states. Used inside the BDF modal.
// Its width is that of the longest label it can show, so a state change never
// moves what follows it.
@Component({
  selector: 'app-bdf-row-pill',
  templateUrl: './bdf-row-pill.component.html',
  styleUrls: ['./bdf-row-pill.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [InlineSpinnerComponent, TooltipDirective],
})
export class BdfRowPillComponent {
  private readonly bdfService = inject(BDF_SERVICE, { optional: true }) as BdfService | null;

  /** Smart mode: pill resolves status from this list-row record. */
  readonly item = input<BdfPillSource | null>(null);

  /** Direct mode: explicit BDF status to render. Wins over `item`. */
  readonly status = input<BdfJobStatus | null | undefined>(undefined);

  /** Label override; falls back to the state's own label ("BDF ready", "Queued", …). */
  readonly text = input<string | null>(null);

  /** Badge size: 'xs' (default) and 'xxs' are the table badge size; 'sm' the larger badge. */
  readonly size = input<BdfPillSize>('xs');

  /** Optional tooltip; none by default, since the label already states the state. */
  readonly tooltip = input<string | null>(null);

  /**
   * What activating the badge does, when it is a button's content (e.g. "open
   * BDF generation"): spoken with the state as one phrase, so the button's
   * name is "BDF ready, open BDF generation".
   */
  readonly action = input<string>('');

  /**
   * In smart mode the pill renders only running/done (idle/queued/error are
   * suppressed because the list row has its own UI for those). In direct mode
   * the caller decides — set `showAll` true to render all five states.
   */
  readonly showAll = input<boolean>(false);

  readonly effectiveStatus = computed<BdfJobStatus | null>(() => {
    const direct = this.status();
    if (direct !== undefined) return direct ?? null;
    return this.smartIndicator();
  });

  readonly visible = computed<boolean>(() => {
    const s = this.effectiveStatus();
    if (!s) return false;
    if (this.showAll() || this.status() !== undefined) return true;
    return s === 'running' || s === 'done';
  });

  readonly effectiveText = computed<string>(() => {
    const override = this.text();
    if (override !== null) return override;
    return labelFor(this.effectiveStatus(), this.status() === undefined && !this.showAll());
  });

  readonly effectiveTooltip = computed<string>(() => this.tooltip() ?? '');

  /**
   * Labels the badge reserves room for, so a state that changes in front of
   * the user keeps the badge's width: only queued and running swap in place
   * (in the modal). Every other state, and a list row's badge, fits its own label.
   */
  readonly reservedLabels = computed<string[]>(() => {
    if (this.text() !== null) return [];
    const smart = this.status() === undefined && !this.showAll();
    if (smart) return [];
    const status = this.effectiveStatus();
    if (status !== 'queued' && status !== 'running') return [];
    const current = this.effectiveText();
    return (['queued', 'running'] as BdfJobStatus[]).map((s) => labelFor(s, false)).filter((l) => l !== current);
  });

  /** Resolve a 'running' / 'done' indicator from the project-list-row record. */
  private smartIndicator(): BdfJobStatus | null {
    const it = this.item();
    if (!it) return null;
    const persistedJobs: Array<BdfJob | null> = it.children && it.children.length > 0
      ? it.children.map((c) => this.live(c.id, c.bdfJob))
      : [this.live(it.id, it.bdfJob)];
    const present = persistedJobs.filter((j): j is BdfJob => !!j);
    if (present.length === 0) return null;
    if (present.some((j) => j.status === 'queued' || j.status === 'running')) return 'running';
    if (present.every((j) => j.status === 'done' && (!!j.s3Key || !!j.localPath))) return 'done';
    return null;
  }

  private live(id: string, persisted: BdfPillSource['bdfJob']): BdfJob | null {
    return this.bdfService?.bdfStatusFor(id)() ?? (persisted as BdfJob | null) ?? null;
  }
}

/**
 * The label for a state. In a list row the badge stands next to a project's
 * name, so it says what it is about ("BDF ready"); in the BDF dialog the row
 * already says BDF, so the state alone ("Ready").
 */
function labelFor(status: BdfJobStatus | null, inList: boolean): string {
  switch (status) {
    case 'running': return inList ? 'BDF running' : 'Generating';
    case 'done':    return inList ? 'BDF ready' : 'Ready';
    case 'queued':  return inList ? 'BDF queued' : 'Queued';
    case 'error':   return inList ? 'BDF failed' : 'Failed';
    case 'idle':    return inList ? 'No BDF' : 'Not generated';
    default:        return '';
  }
}
